import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  FilePlus, RefreshCw, Send, CheckSquare, CheckCircle, XCircle,
  Flag, Award, PauseCircle, HelpCircle, Clipboard, Clock, Loader2,
  ChevronDown, ChevronUp, ExternalLink, Gavel
} from 'lucide-react';

const iconMap = {
  'file-plus': FilePlus,
  'refresh-cw': RefreshCw,
  'send': Send,
  'check-square': CheckSquare,
  'check-circle': CheckCircle,
  'gavel': Gavel,
  'flag': Flag,
  'award': Award,
  'pause-circle': PauseCircle,
  'help-circle': HelpCircle,
  'clipboard': Clipboard,
};

const colorMap = {
  blue: 'bg-blue-500',
  indigo: 'bg-indigo-500',
  purple: 'bg-purple-500',
  teal: 'bg-teal-500',
  amber: 'bg-amber-500',
  green: 'bg-green-500',
  lime: 'bg-lime-500',
  red: 'bg-red-500',
  orange: 'bg-orange-500',
  gray: 'bg-gray-400',
};

const colorBorderMap = {
  blue: 'border-blue-500',
  indigo: 'border-indigo-500',
  purple: 'border-purple-500',
  teal: 'border-teal-500',
  amber: 'border-amber-500',
  green: 'border-green-500',
  lime: 'border-lime-500',
  red: 'border-red-500',
  orange: 'border-orange-500',
  gray: 'border-gray-400',
};

const ShopDrawingTimeline = ({ packageId, projectId }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [timeline, setTimeline] = useState([]);
  const [packageRef, setPackageRef] = useState('');
  const [expandedItems, setExpandedItems] = useState({});
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadTimeline();
  }, [packageId]);

  const loadTimeline = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/shop-drawings/packages/${packageId}/timeline`);
      setTimeline(response.data.timeline || []);
      setPackageRef(response.data.package_ref || '');
    } catch (error) {
      console.error('Failed to load timeline:', error);
      toast.error('Failed to load timeline');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedItems(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleItemClick = (item) => {
    if (item.link_type === 'rfi' && item.link_id) {
      navigate(`/projects/${projectId}/rfi/${item.link_id}`);
    } else if (item.link_type === 'si' && item.link_id) {
      navigate(`/si/${item.link_id}`);
    } else if (item.cycle_id) {
      // Scroll to cycle details or expand
      toggleExpand(item.id);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filterOptions = [
    { value: 'all', label: 'All Events' },
    { value: 'cycle', label: 'Cycles Only' },
    { value: 'decisions', label: 'Decisions' },
    { value: 'related', label: 'Related Items' },
  ];

  const filteredTimeline = timeline.filter(item => {
    if (filter === 'all') return true;
    if (filter === 'cycle') return item.type.includes('cycle') || item.type.includes('contractor') || item.type.includes('consultant');
    if (filter === 'decisions') return item.type === 'consultant_decision' || item.type === 'approved';
    if (filter === 'related') return item.type === 'rfi_created' || item.type === 'si_created';
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (timeline.length === 0) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Clock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-700">No Timeline Events</h3>
          <p className="text-slate-500">Timeline events will appear here as the package progresses</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with filter */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-800">Package Timeline</h3>
          <p className="text-sm text-slate-500">{timeline.length} events for {packageRef}</p>
        </div>
        <div className="flex items-center gap-2">
          {filterOptions.map(opt => (
            <Button
              key={opt.value}
              variant={filter === opt.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(opt.value)}
              className="text-xs"
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-gradient-to-b from-slate-300 via-slate-200 to-transparent" />

        <div className="space-y-4">
          {filteredTimeline.map((item, index) => {
            const IconComponent = iconMap[item.icon] || Clock;
            const bgColor = colorMap[item.color] || 'bg-slate-400';
            const borderColor = colorBorderMap[item.color] || 'border-slate-400';
            const isExpanded = expandedItems[item.id];
            const hasDetails = item.description || item.rejection_details || item.decision;
            const isClickable = item.link_type || hasDetails;

            return (
              <div
                key={item.id}
                className={`relative pl-16 ${isClickable ? 'cursor-pointer' : ''}`}
                onClick={() => isClickable && handleItemClick(item)}
              >
                {/* Icon circle */}
                <div className={`absolute left-3 w-7 h-7 rounded-full ${bgColor} flex items-center justify-center shadow-lg ring-4 ring-white`}>
                  <IconComponent className="h-3.5 w-3.5 text-white" />
                </div>

                {/* Content card */}
                <Card className={`hover:shadow-md transition-all duration-200 border-l-4 ${borderColor} ${isClickable ? 'hover:bg-slate-50' : ''}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold text-slate-900">{item.title}</h4>
                          {item.cycle_number && (
                            <Badge variant="outline" className="text-xs">
                              Cycle {item.cycle_number}
                            </Badge>
                          )}
                          {item.decision && (
                            <Badge className={`text-xs ${
                              item.decision === 'Approved' ? 'bg-green-100 text-green-700' :
                              item.decision === 'Approved with Comments' ? 'bg-lime-100 text-lime-700' :
                              item.decision === 'Rejected' ? 'bg-red-100 text-red-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {item.decision}
                            </Badge>
                          )}
                          {item.link_type && (
                            <ExternalLink className="h-3 w-3 text-slate-400" />
                          )}
                        </div>

                        {/* Description (always visible) */}
                        {item.description && (
                          <p className="text-sm text-slate-600 line-clamp-2">{item.description}</p>
                        )}

                        {/* Expanded details */}
                        {isExpanded && hasDetails && (
                          <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
                            {item.rejection_reason && (
                              <div>
                                <span className="text-xs font-medium text-slate-500">Rejection Category:</span>
                                <p className="text-sm text-red-600">{item.rejection_reason}</p>
                              </div>
                            )}
                            {item.rejection_details && (
                              <div>
                                <span className="text-xs font-medium text-slate-500">Details:</span>
                                <p className="text-sm text-slate-700">{item.rejection_details}</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Date and user */}
                        <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatDate(item.date)}
                          </span>
                          {item.user && (
                            <span>by {item.user}</span>
                          )}
                        </div>
                      </div>

                      {/* Expand button */}
                      {hasDetails && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(item.id);
                          }}
                        >
                          {isExpanded ? (
                            <ChevronUp className="h-4 w-4 text-slate-400" />
                          ) : (
                            <ChevronDown className="h-4 w-4 text-slate-400" />
                          )}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary stats */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="p-4">
          <div className="grid grid-cols-4 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-slate-700">
                {timeline.filter(t => t.type.includes('cycle_started')).length}
              </p>
              <p className="text-xs text-slate-500">Cycles</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">
                {timeline.filter(t => t.decision === 'Approved' || t.decision === 'Approved with Comments').length}
              </p>
              <p className="text-xs text-slate-500">Approvals</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">
                {timeline.filter(t => t.decision === 'Rejected').length}
              </p>
              <p className="text-xs text-slate-500">Rejections</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">
                {timeline.filter(t => t.type === 'rfi_created' || t.type === 'si_created').length}
              </p>
              <p className="text-xs text-slate-500">Related Items</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ShopDrawingTimeline;
